import { Card } from '@/components/ui/Card';
import { planCycleColor } from './planColors';
import { condensedWeeks, dmaxWeeks, dminWeeks, recoveryWeeks, type CycleAllocation } from './planAllocation';

const Bar = ({ label, weeks, segments }: { label: string; weeks: number; segments: { weeks: number; removed: number; name: string; color: string }[] }) => {
  const total = segments.reduce((s, seg) => s + seg.weeks + seg.removed, 0);
  return (
    <div className="flex items-center gap-3">
      <span className="w-52 shrink-0 text-xs font-medium text-gray-600 dark:text-gray-300">{label}</span>
      <div className="flex h-4 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        {total > 0 &&
          segments.map((seg, i) => (
            <div key={i} className="flex" style={{ width: `${((seg.weeks + seg.removed) / total) * 100}%` }}>
              {seg.removed > 0 && (
                <div
                  title={`${seg.name} · ${seg.removed} semaine(s) retirée(s)`}
                  style={{
                    width: `${(seg.removed / (seg.weeks + seg.removed)) * 100}%`,
                    backgroundImage: `repeating-linear-gradient(45deg, ${seg.color}55 0 4px, transparent 4px 8px)`,
                  }}
                />
              )}
              {seg.weeks > 0 && (
                <div
                  title={`${seg.name} · ${seg.weeks} semaine(s)`}
                  style={{ width: `${(seg.weeks / (seg.weeks + seg.removed)) * 100}%`, backgroundColor: seg.color }}
                />
              )}
            </div>
          ))}
      </div>
      <span className="w-16 shrink-0 text-right text-xs text-gray-500">{weeks} sem.</span>
    </div>
  );
};

const AllocationBars = ({ alloc, thresholdWeeks }: { alloc: CycleAllocation[]; thresholdWeeks: number | null }) => {
  const condensed = thresholdWeeks === null ? null : condensedWeeks(alloc, thresholdWeeks);
  const recovery = recoveryWeeks(alloc);
  return (
    <Card className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Allocation</p>
      <p className="text-xs text-gray-400">Chaque bloc = un cycle, largeur = ses semaines. Les parties hachurées sont retirées.</p>
      <Bar
        label="Préparation idéale (max)"
        weeks={dmaxWeeks(alloc)}
        segments={alloc.map((c, i) => ({ weeks: c.effectiveMaxWeeks, removed: 0, name: c.name, color: planCycleColor(i) }))}
      />
      <Bar
        label="Préparation minimale"
        weeks={dminWeeks(alloc)}
        segments={alloc.map((c, i) => ({ weeks: c.role === 'RECOVERY' ? c.effectiveMaxWeeks : c.minWeeks, removed: 0, name: c.name, color: planCycleColor(i) }))}
      />
      {condensed && (
        <Bar
          label="Préparation condensée la plus courte"
          weeks={thresholdWeeks!}
          segments={alloc.map((c, i) => ({ weeks: condensed[i], removed: (c.role === 'RECOVERY' ? c.effectiveMaxWeeks : c.minWeeks) - condensed[i], name: c.name, color: planCycleColor(i) }))}
        />
      )}
      {recovery > 0 && (
        <p className="text-xs text-gray-400">
          + {recovery} semaine(s) de récupération après la course, jamais réduites ni condensées.
        </p>
      )}
    </Card>
  );
};

export default AllocationBars;
